import * as ko from "knockout";
import { BaseCrop, IBaseCropYear } from "../Models/Crops/BaseCrop";
import { groupBy, map, orderBy, forEach, isEmpty } from "lodash-es";
import "footable-npm";
import "bootstrap"

/*
 * Used for grouping the crops under a year.
 */
class YearCropCollection<T> {
    year: number;
    collection: T[];

    constructor(year: number, collection: T[]) {
        this.year = year;
        this.collection = collection;
    }
}

export class GenericCropsViewModel<T extends IBaseCropYear>  {

    //Custom filter function to apply url filters.
    customfilterFunction: (item: any) => boolean;

    //List of active custom filters.
    customFilters = ko.observableArray<string>();

    //Url query for the custom filter.
    customFilterQuery = ko.observable<string>();

    //Full lists of the type eg. Crops for the grower, this should ONLY be updated through ajax.
    completelist = ko.observableArray<T>();

    //Show spinner or not.
    loading = ko.observable<boolean>(true);

    //Active filters collection
    private seedSubtypeFilters = ko.observableArray<string>();

    //url parmeter for storing filters between pageloads.
    private filterQueryPrefix: string = "seedType";

    /*
     * Subset of completelist with applied filter.
     */
    private filteredList: KnockoutComputed<T[]> = ko.computed(() => {
        return ko.utils.arrayFilter(this.completelist(), (item: T) => {
            const defaultFilter = this.seedSubtypeFilters().length === 0 || this.seedSubtypeFilters().indexOf(item.Specie.Group) > -1;
            //if custom filter function exits and set to active --> invoke the function to filter along with the standard function.
            if (this.customfilterFunction != undefined) {
                return (this.customfilterFunction(item) || this.customFilters().length === 0) && defaultFilter;
            } else {
                return defaultFilter;
            }

        });
    });

    /*
     * Filter options, used to render filter dropwon. 
     */
    private seedSubtypes: KnockoutComputed<string[]> = ko.computed(() => {
        return Array.from(new Set(this.completelist().map((item: T) => item.Specie.Group)));
    });

    /*
     * Collection used for the graphic table representations.
     */
    private groupedByYear: KnockoutComputed<YearCropCollection<T>[]> = ko.computed(() => {
        let list = new Array<YearCropCollection<T>>();
        var groupedList = groupBy(orderBy(this.filteredList(), c => c.CropYear), (item: T) => item.CropYear);
        forEach(groupedList, e => {
            const year = e[0].CropYear;
            list.push(new YearCropCollection(year, e));
        });
        return orderBy(list, c => c.year, 'desc');
    });


    constructor() {
        //Subscribe to subset of crops change to keep footable up to date.
        this.filteredList.subscribe(() => {
            ($('.toBeFoo') as any).footable();
        });

        //Subscribe to filters to keep the url up to date.
        this.seedSubtypeFilters.subscribe((value: string[]) => {
            this.loading(true);
            const url = new URL(window.location.href);
            if (value.length > 0) {
                url.searchParams.set(this.filterQueryPrefix, value.join(","));
            } else {
                url.searchParams.delete(this.filterQueryPrefix);
            }

            //Replace url encoded commas: %2C with accutual commas as its prettier.
            const uriWithoutCommas = url.toString().split('%2C').join(",");
            history.pushState(null, '', uriWithoutCommas);
            this.loading(false);
        });


        /*
         * Subscribe to Seedsubtypes to apply filter from url if any or default to all filters active. 
         */
        this.seedSubtypes.subscribe((value: string[]) => {
            const url = new URL(window.location.href);
            const filterQuery = url.searchParams.get(this.filterQueryPrefix);

            if (!isEmpty(filterQuery)) {
                const filter = filterQuery.split(',');
                this.seedSubtypeFilters(filter);
            }
        });
    }

    /**
     * Removes/disables an active filter from the filter collection 'seedSubtypeFilters'.
     * @param filter
     */
    removeFilter(filter: string): void {
        this.seedSubtypeFilters.remove(filter);
    }
    /**
     *  Removes ALL filters
     */
    clearFilter(): void {
        this.seedSubtypeFilters.removeAll();
        forEach(this.customFilters(), customFilter => {
            this.removeFilterCustomFilter(customFilter);
        });
    }

    /**
     * Removes a given custom filter and updates the url
     * @param filter
     */
    removeFilterCustomFilter(filter: string): void {
        this.customFilters.remove(filter);

        const url = new URL(window.location.href);
        url.searchParams.delete(this.customFilterQuery());
        //Replace url encoded commas: %2C with accutual commas as its prettier.
        const uriWithoutCommas = url.toString().split('%2C').join(",");
        history.pushState(null, '', uriWithoutCommas);

    }

    /**
     * Multiplies the input number with the given factor
     * @param input
     * @param factor
     */
    multiply(input: number, factor: number): number {
        return input * factor;
    }

    /**
     * Multiplies number and returns it as a string based on the locale and digits.
     * Returns default value if input was null.
     * @param input To be muliplied with the factor
     * @param factor Factor to multiply input with
     * @param digits Number of digigts on the return value (requires locale)
     * @param locale Locale string to return.
     * @param defaultValue Default value if input was null.
     */
    multiplyAndRoundToLocale(input: number, factor: number, digits: number, locale: string, defaultValue: string, minimumDigits?: number): string {
        if (input == null)
            return defaultValue;

        const result = input * factor;
        if (!isEmpty(locale)) {

            return digits != null
                ? (minimumDigits != null ? result.toLocaleString(locale, { maximumFractionDigits: digits, minimumFractionDigits: minimumDigits }) : result.toLocaleString(locale, { maximumFractionDigits: digits })) 
                : result.toLocaleString(locale);
        }

        return result.toString();
    }
}
